import { cn } from "lib/utils";
import { forwardRef, type InputHTMLAttributes } from "react";

export type RadioOption = {
  label: string;
  value: string;
};

export type RadioGroupProps = {
  options: RadioOption[];
  name: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  orientation?: "horizontal" | "vertical";
} & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "className" | "type" | "value" | "onChange" | "onBlur" | "name"
>;

/**
 * 純粋な RadioGroup リーフ。options から複数の radio を描画する。
 * @param {RadioGroupProps} props RadioGroup の props
 * @return {JSX.Element} ラジオボタングループ
 */
export const RadioGroup = forwardRef<HTMLInputElement, RadioGroupProps>(
  (
    {
      options,
      name,
      value,
      onChange,
      onBlur,
      orientation = "horizontal",
      ...rest
    },
    ref,
  ) => {
    return (
      <div
        className={cn(
          "flex",
          orientation === "horizontal"
            ? "flex-row items-center gap-4"
            : "flex-col gap-2",
        )}
      >
        {options.map((option, index) => (
          <label
            key={option.value}
            className="label cursor-pointer gap-2 py-0"
            htmlFor={`${name}-${option.value}`}
          >
            <input
              ref={index === 0 ? ref : undefined}
              id={`${name}-${option.value}`}
              type="radio"
              className="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange?.(option.value)}
              onBlur={onBlur}
              {...rest}
            />
            <span className="label-text">{option.label}</span>
          </label>
        ))}
      </div>
    );
  },
);
RadioGroup.displayName = "RadioGroup";
